import { v4 as uuidV4 } from 'uuid'

import graphql from 'api/graphql'
import appGraphql from 'api/app_graphql'
import grooveAPI from 'api/groove'
import { oauthTokenSelector } from 'selectors/app'
import debug from 'util/debug'
import storage from 'util/storage'
import { getTabId } from 'util/tabId'
import { getVersion } from 'util/version'
import config from 'config'
import integrationApi from 'ducks/integrations/api'
import { DEFAULT_MODULES } from './modules'

function transformData(data, store, options) {
  return options.transformResponse
    ? options.transformResponse(data, store, options)
    : data
}

async function executeRequest(requestFn, store, variables, options) {
  const { dispatch, getState } = store
  const isCaching =
    options.cacheKey && config.isDevelopment && storage.get('cacheRequests')
  const lsKey = `requestCache-${options.cacheKey}`
  if (isCaching) {
    const cachedData = storage.get(lsKey, undefined)
    if (cachedData !== undefined) {
      return cachedData
    }
  }
  const data = await requestFn({
    variables,
    dispatch,
    getState,
  })
  if (isCaching) storage.set(lsKey, data)
  return data
}

function createRequestAction(phase, payload, options) {
  const action = {}
  const modules = options.modules || DEFAULT_MODULES
  modules.forEach(moduleFn => moduleFn(phase, action, payload, options))
  return action
}

export const doRequest = (
  requestType,
  promiseFunction,
  variables,
  options = {}
) => {
  return async (dispatch, getState) => {
    const { transformVariables, onBeforeSuccessAction, throwOnError } = options
    const requestId = options.requestId || uuidV4()
    let requestParameters = variables
    if (!options.skipStarted) {
      dispatch(
        createRequestAction('STARTED', variables, {
          ...options,
          requestType,
          requestParameters,
          requestId,
        })
      )
    }
    let successAction
    if (transformVariables) {
      requestParameters = transformVariables(requestParameters)
    }

    const store = { dispatch, getState }
    try {
      const data = await executeRequest(
        promiseFunction,
        store,
        requestParameters,
        options
      )
      const transformedData = transformData(data, store, options) || {}

      successAction = createRequestAction('SUCCESS', transformedData, {
        ...options,
        requestType,
        requestParameters,
        requestId,
      })
      if (onBeforeSuccessAction) {
        await onBeforeSuccessAction(transformedData)
      }
      dispatch(successAction)
      return transformedData
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      debug(`${options.logPrefix || 'REQUEST'}: ${requestType}_FAILED`, error)
      dispatch(
        createRequestAction(
          'FAILED',
          { error, successAction },
          {
            ...options,
            requestType,
            requestParameters,
            requestId,
          }
        )
      )

      // HACK (jscheel): We have a ton of legacy code that does not listen for
      // this error. However, it is useful if you need to chain a promise catch.
      // This way, you can use promise.catch if you need to by passing this in.
      if (throwOnError) {
        throw error
      }
      return null
    }
  }
}

export const doGraphqlRequest = (
  requestType,
  queryOrMutation,
  variables,
  options = {}
) => {
  return doRequest(
    requestType,
    async ({ variables: transformedVariables, getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)
      const graphqlFunc =
        options.graphqlFunc || (options.app === true ? appGraphql : graphql)
      const res = await graphqlFunc(
        token,
        queryOrMutation,
        transformedVariables
      )
      return res.json.data
    },
    variables,
    {
      ...options,
      logPrefix: 'GQL',
    }
  )
}

export const doIntegrationsReadRequest = (
  requestType,
  path,
  variables,
  options = {}
) => {
  return doRequest(
    requestType,
    async ({ variables: transformedVariables, getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)
      const res = await integrationApi.get(token, path, transformedVariables)
      return res.json
    },
    variables,
    {
      ...options,
      logPrefix: 'INTEGRATIONS',
    }
  )
}

export const doIntegrationsWriteRequest = (
  requestType,
  path,
  variables,
  options = {}
) => {
  const method = options.method || 'post'
  return doRequest(
    requestType,
    async ({ variables: transformedVariables, getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)

      const { json } = await integrationApi.send(
        method,
        token,
        path,
        options.params,
        JSON.stringify(transformedVariables)
      )
      return json
    },
    variables,
    {
      ...options,
      logPrefix: 'INTEGRATIONS',
    }
  )
}

export function doAppGraphqlRequest(
  requestType,
  queryOrMutation,
  variables,
  options
) {
  return doGraphqlRequest(requestType, queryOrMutation, variables, {
    ...options,
    app: true,
  })
}

export const doSdkRequest = (
  requestType,
  sdkPromiseFunction,
  variables = {},
  options = {}
) => {
  return doRequest(requestType, sdkPromiseFunction, variables, {
    ...options,
    logPrefix: 'SDK',
  })
}

export const doApiReadRequest = (
  requestType,
  path,
  variables,
  options = {}
) => {
  return doRequest(
    requestType,
    async ({ variables: transformedVariables, getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)
      const res = await grooveAPI.get(token, path, transformedVariables)
      return res.json
    },
    variables,
    {
      ...options,
      logPrefix: 'API',
    }
  )
}

export const doApiPutRequest = (requestType, path, variables, options = {}) => {
  return doRequest(
    requestType,
    async ({ variables: transformedVariables, getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)

      const { json } = await grooveAPI.put(
        token,
        path,
        options.params,
        JSON.stringify(transformedVariables)
      )
      return json
    },
    variables,
    options
  )
}

export const doApiWriteRequest = (
  requestType,
  path,
  variables,
  options = {}
) => {
  const method = options.method || 'post'
  return doRequest(
    requestType,
    async ({ variables: transformedVariables, getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)

      const { json } = await grooveAPI.send(
        method,
        token,
        path,
        options.params,
        JSON.stringify(transformedVariables)
      )
      return json
    },
    variables,
    options
  )
}

export function doAttachementRequest(
  requestType,
  path,
  attachments,
  options = {}
) {
  return doRequest(
    requestType,
    async ({ getState }) => {
      const state = getState()
      const token = oauthTokenSelector(state)
      const formData = new FormData()
      attachments.forEach(attachment => formData.append('files[]', attachment))
      return fetch(`${config.uploaderUrl}${path}`, {
        body: formData,
        cache: 'no-cache',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Request-Id': uuidV4(),
          'X-Client-Tab-Id': getTabId(),
          'X-Client-App-Version': getVersion(),
        },
        method: 'POST',
      })
    },
    {},
    options
  )
}
